/**
 * The rules behind naming candidates in an outreach email.
 *
 * Split from the picker component so they can be tested without React: what
 * goes into a message to an employer, and — the part worth pinning — what never
 * does.
 */

import { experienceLabel } from "../taxonomy/experience";

/**
 * The three candidates a person decides to name in an outreach email.
 *
 * Every first email this product sends says "tenemos candidatos que encajan con
 * ese perfil y que están disponibles ahora mismo", and until now there was
 * nothing behind that sentence: no way to attach a shortlist, no link from an
 * employer's vacancy to anybody, and so a promise the product could not keep.
 *
 * ── This is not matching, and the difference is the whole point ──────
 *
 * The deleted matching feature ranked every candidate in the database against a
 * vacancy nobody had applied to and wrote its verdicts to a table parallel to
 * the real one — a relationship neither side had agreed to. Nothing here does
 * that. There is no scoring, no ranking, no automatic suggestion and no stored
 * edge between a vacancy and a person: an operator searches by name or by
 * discipline, reads the profiles, and picks. What gets recorded is the message
 * that was actually sent and a log entry naming who was named in it — a record
 * of an act somebody took, which is exactly what `prospect_activity` is for.
 *
 * ── What goes in the email ───────────────────────────────────────────
 *
 * Only what the candidate themselves put on their profile, and never a contact
 * detail. An employer receiving a name, what they studied, where they are and
 * how long they have worked in the field can say "yes, send me that one"; an
 * employer receiving three CVs and three email addresses has been handed the
 * business, and the candidate never agreed to that.
 *
 * ── Read from the columns that exist ─────────────────────────────────
 *
 * This used to read `headline`, `current_position`, `years_experience`, `city`
 * and `country` off the row. `talents` has none of them — the questionnaire
 * keeps city, country, degree and the two experience ranges inside its JSON —
 * so every candidate in the picker read "sin titular" and every block reached
 * the employer as a bare list of names. The readings below are the ones the
 * rest of the panel uses (`summarizeTalent`) and the site's applicant card uses
 * (`src/common/applicant.ts`): city and country from the questionnaire, the
 * legacy `location` column when those are missing.
 */

export interface ShortlistCandidate {
    id: string;
    full_name?: string | null;
    /**
     * A storage key for their photo, for the picker's own list — never for the
     * message. `shortlistBlock` names the fields it prints one by one, so a
     * field added here cannot leak into an employer's inbox by being present.
     */
    photo_url?: string | null;
    /** The legacy top-level column; the questionnaire's city and country win. */
    location?: string | null;
    questionnaire?: {
        city?: unknown;
        country?: unknown;
        degree?: unknown;
        years_experience_sustainability?: unknown;
        years_experience_total?: unknown;
    } | null;
}

/** Long enough to mean something, short enough to stay quick. */
export const MIN_QUERY = 2;
export const RESULTS = 8;

function text(value: unknown): string | null {
    return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function answers(candidate: ShortlistCandidate): NonNullable<ShortlistCandidate["questionnaire"]> {
    const q = candidate.questionnaire;
    return q && typeof q === "object" ? q : {};
}

export function placeOf(candidate: ShortlistCandidate): string {
    const q = answers(candidate);
    return [text(q.city), text(q.country)].filter(Boolean).join(", ")
        || (text(candidate.location) ?? "");
}

/**
 * What they studied, in their own words — the first degree they listed.
 *
 * The nearest thing to a title this table holds. There is no job title or
 * headline column, and inventing one from their areas would put a claim in an
 * employer's inbox that the candidate never wrote.
 */
export function degreeOf(candidate: ShortlistCandidate): string {
    const degree = answers(candidate).degree;
    const list = Array.isArray(degree) ? degree : [degree];
    return list.map(text).find((d): d is string => d !== null) ?? "";
}

/**
 * How long they have worked in the field, saying which field.
 *
 * Sustainability experience when they gave one, total experience otherwise, and
 * the sentence names which of the two it is: "4–6 años" alone is a different
 * claim depending on the question it answered. A sustainability answer of "0"
 * falls through to the total rather than telling an employer "sin experiencia".
 */
export function experienceOf(candidate: ShortlistCandidate): string {
    const q = answers(candidate);
    const spoken = (range: string) => (range === "0-1" ? "menos de un año" : experienceLabel(range) ?? range);
    const inField = text(q.years_experience_sustainability);
    if (inField && inField !== "0") return `${spoken(inField)} en sostenibilidad`;
    const total = text(q.years_experience_total);
    if (total && total !== "0") return `${spoken(total)} de experiencia`;
    return "";
}

export function shortlistLabel(candidate: ShortlistCandidate): string {
    return (candidate.full_name ?? "").trim() || "(sin nombre)";
}

/**
 * The line under a name in the picker — exactly the facts the email block will
 * carry, so what the operator reads while choosing is what the employer reads.
 */
export function shortlistFacts(candidate: ShortlistCandidate): string[] {
    return [degreeOf(candidate), placeOf(candidate), experienceOf(candidate)].filter(Boolean);
}

/**
 * The block appended to the message body.
 *
 * Deliberately plain text and deliberately editable once it lands: it goes into
 * the same textarea the operator is writing in, so the sentence before it can be
 * rewritten to match. A block that inserted itself at send time would be one
 * nobody had read.
 *
 * No email, no phone, no CV link. See the note above.
 */
export function shortlistBlock(candidates: ShortlistCandidate[]): string {
    if (candidates.length === 0) return "";
    const lines = candidates.map(candidate =>
        `· ${[shortlistLabel(candidate), ...shortlistFacts(candidate)].join(" — ")}`
    );
    return [
        candidates.length === 1
            ? "Un perfil que encaja:"
            : `${candidates.length} perfiles que encajan:`,
        ...lines,
        "",
        "Si alguno te interesa te paso su CV y hablamos."
    ].join("\n");
}
